import {
  hasResolvedDocumentValue,
  useDocument,
  useDocumentsIndividually,
} from "@shared/components/providers/SupergrainProvider"
import type { Group } from "@shared/models/group"
import type { Planbook } from "@shared/models/planbook"
import type { DocumentHandle } from "@supergrain/silo"
import ENV from "cc-frontend/config/environment"
import { extractCardStackTemplateIds } from "cc-frontend/utils/filter-dates/course"
import { isCachedMembershipActiveInclAncestors } from "cc-frontend/utils/group"
import { useEffect, useRef } from "react"

export interface TemplateChoice {
  id: string
  title: string
}

export interface SharedTemplateGroup {
  groupId: string
  groupTitle: string
  templates: Array<TemplateChoice>
}

export interface TemplateChoices {
  /** Templates living in this planbook — saved to the library, or applied to a course's week. */
  mine: Array<TemplateChoice>
  /** One entry per group that publishes templates, in membership order with Common Curriculum last. */
  shared: Array<SharedTemplateGroup>
  /** Some part of the chain is still arriving; the lists so far are real, just not final. */
  isLoading: boolean
  /**
   * Some part of the chain came back an error, so the lists are short by an unknown amount.
   * Distinct from `isLoading`: waiting resolves itself, this does not.
   */
  hasError: boolean
}

const CC_TEMPLATE_GROUP_ID: string = ENV.CC_TEMPLATE_GROUP_ID

/** The one template that is always in reach: the blank one the app itself publishes. */
export const BLANK_TEMPLATE_ID: string = ENV.BLANK_TEMPLATE_ID

/**
 * Every template the user can replace a lesson or unit with, split into their own and the ones
 * their groups publish.
 *
 * A group's templates are not copied into the planbook when they get used: assigning one to a
 * course's week stores the *group's* card-stack id on the course dates
 * (`create-main.ts`'s `chooseTemplate`). So a template can reach `mine` through a course while
 * genuinely belonging to a group, and the split has to be resolved by id — group wins, so a
 * shared template in use shows once, under the group that publishes it.
 */
export function useTemplateChoices(planbook: Planbook): TemplateChoices {
  // Every course, not just the one in hand: the legacy menu offered the whole planbook's
  // templates, and a template applied to one course is just as usable in another.
  const courseIds = planbook.relationships.courses.data.map((linkage) => linkage.id)
  const courseHandles = useDocumentsIndividually("course", courseIds)
  const courses = resolved(courseHandles)

  const ownerId = planbook.relationships.owner.data.id
  const ownerHandle = useDocument("user", ownerId)
  // The planbook's owner, not the viewer: a shared planbook offers the templates its owner's
  // school publishes, which is what the gallery has always shown.
  const membershipGroupIds =
    ownerHandle.value?.attributes.cachedMemberships
      .filter(isCachedMembershipActiveInclAncestors)
      .map((membership) => membership.groupId) ?? []
  const groupIds = unique([...membershipGroupIds, CC_TEMPLATE_GROUP_ID])

  const groupHandles = useDocumentsIndividually("group", groupIds)
  const groups = resolved(groupHandles)

  const groupTemplatePlanbookIds = compact(groups.map(templatePlanbookIdOf))
  const groupTemplatePlanbooks = useDocumentsIndividually("planbook", groupTemplatePlanbookIds)
  const templatePlanbookById = new Map(
    resolved(groupTemplatePlanbooks).map((templatePlanbook) => [
      templatePlanbook.id,
      templatePlanbook,
    ]),
  )

  const sharedIdsByGroup = groups.map((group) => {
    const templatePlanbookId = templatePlanbookIdOf(group)
    const templatePlanbook =
      templatePlanbookId === null ? undefined : templatePlanbookById.get(templatePlanbookId)
    return {
      groupId: group.id,
      groupTitle: group.attributes.title,
      templateIds:
        templatePlanbook?.relationships.cardStackTemplates.data.map((linkage) => linkage.id) ?? [],
    }
  })

  const myIds = unique([
    ...planbook.relationships.cardStackTemplates.data.map((linkage) => linkage.id),
    ...courses.flatMap((course) => extractCardStackTemplateIds(course)),
  ])
  const sharedIds = unique(sharedIdsByGroup.flatMap((group) => group.templateIds))

  // Summaries, not the card stacks themselves: this menu shows names, and a group that
  // publishes dozens of templates would otherwise pull every lesson body across on open.
  // The one template a row actually pastes is fetched by the press (`store.find`).
  const summaryIds = unique([...myIds, ...sharedIds])
  const summaryHandles = useDocumentsIndividually("card-stack-summary", summaryIds)
  const titleById = new Map(
    resolved(summaryHandles).map((summary) => [
      summary.id,
      summary.attributes.title ?? "Untitled Template",
    ]),
  )
  const choicesFor = (ids: Array<string>): Array<TemplateChoice> =>
    ids.flatMap((id) => {
      const title = titleById.get(id)
      return title === undefined ? [] : [{ id, title }]
    })

  const sharedIdSet = new Set(sharedIds)
  const shared = sharedIdsByGroup
    .map((group) => ({
      groupId: group.groupId,
      groupTitle: group.groupTitle,
      templates: choicesFor(group.templateIds),
    }))
    .filter((group) => group.templates.length > 0)

  const documents = [
    { type: "user", id: ownerId, handle: ownerHandle },
    ...withIds("course", courseIds, courseHandles),
    ...withIds("group", groupIds, groupHandles),
    ...withIds("planbook", groupTemplatePlanbookIds, groupTemplatePlanbooks),
    ...withIds("card-stack-summary", summaryIds, summaryHandles),
  ]
  useLogTemplateChoiceErrors(documents)

  return {
    mine: choicesFor(myIds.filter((id) => !sharedIdSet.has(id))),
    shared,
    isLoading: documents.some(({ handle }) => handle.status === "pending"),
    hasError: documents.some(({ handle }) => handle.status === "error"),
  }
}

/**
 * The planbook holding a group's published templates, if it has one. A group without one can
 * say so two ways — a null relationship, or a present relationship whose `data` is null — and
 * a group that publishes nothing is ordinary, not an error.
 */
function templatePlanbookIdOf(group: Group): string | null {
  return group.relationships.cardStackTemplatesPlanbook?.data?.id ?? null
}

function resolved<T extends { attributes: unknown }>(handles: Array<DocumentHandle<T>>): Array<T> {
  return handles.flatMap((handle) => (hasResolvedDocumentValue(handle) ? [handle.value] : []))
}

interface IdentifiedDocument {
  type: string
  id: string
  handle: DocumentHandle<unknown>
}

function withIds<T>(
  type: string,
  ids: Array<string>,
  handles: Array<DocumentHandle<T>>,
): Array<IdentifiedDocument> {
  return handles.map((handle, index) => ({ type, id: ids[index], handle }))
}

function useLogTemplateChoiceErrors(documents: Array<IdentifiedDocument>): void {
  const logged = useRef(new Set<string>())

  useEffect(() => {
    for (const { type, id, handle } of documents) {
      if (handle.status !== "error") continue
      const key = `${type}:${id}`
      if (logged.current.has(key)) continue
      logged.current.add(key)
      console.error(`Replace with Template failed to load ${type} ${id}`, handle.error)
    }
  })
}

function unique(ids: Array<string>): Array<string> {
  return [...new Set(ids)]
}

function compact(ids: Array<string | null>): Array<string> {
  return ids.flatMap((id) => (id === null ? [] : [id]))
}
